/**
 * Hooks du back-office.
 *
 * Toute la logique de données vit ici, jamais dans le JSX (docs/24 §3).
 *
 * ⚠️ Aucun de ces hooks ne protège quoi que ce soit : ils appellent des routes
 * que le serveur garde par `admin.*`. Masquer un écran côté client évite un 403
 * à l'utilisateur, rien de plus (docs/07 §8).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components, paths } from "@/shared/api/generated";
import { adminKeys } from "@/shared/api/query-keys";

export type AdminUser = components["schemas"]["AdminUserResponse"];
export type AdminUserDetail = components["schemas"]["AdminUserDetailResponse"];
export type AuditEntry = components["schemas"]["AuditEntryResponse"];
export type AdminRole = components["schemas"]["RoleResponse"];

/**
 * Filtres de la liste. L'index signature est ce qui les rend utilisables comme
 * clé TanStack Query : deux jeux de filtres différents doivent donner deux
 * caches distincts, et `adminKeys.users` les sérialise tels quels.
 */
export interface UserFilters {
  [key: string]: string | undefined;
  q?: string;
  status?: string;
  role?: string;
}

interface UserPage {
  data: AdminUser[];
  meta: components["schemas"]["PageMeta"];
}

/**
 * Pagination **vers l'avant seulement**, et c'est délibéré.
 *
 * L'API pagine par keyset (docs/08 §4) : elle rend un `next_cursor`, jamais de
 * précédent, parce qu'un curseur désigne la dernière ligne servie et non un
 * rang. Des numéros de page ou un bouton « précédent » promettraient donc une
 * navigation que le serveur ne sait pas faire — on charge la suite, et on
 * revient en haut de liste en changeant un filtre.
 */
export function useAdminUsers(
  filters: UserFilters,
): UseInfiniteQueryResult<InfiniteData<UserPage>, ApiError> {
  return useInfiniteQuery({
    queryKey: adminKeys.users(filters),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/v1/admin/users", {
          params: {
            query: {
              // Les filtres vides sont omis plutôt qu'envoyés à blanc : `?q=`
              // et « pas de recherche » ne veulent pas dire la même chose côté
              // serveur, et le second est ce que l'écran demande vraiment.
              ...(filters.q ? { q: filters.q } : {}),
              ...(filters.status ? { status: [filters.status] } : {}),
              ...(filters.role ? { role: filters.role } : {}),
              ...(pageParam === undefined ? {} : { cursor: pageParam }),
            },
          },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
  });
}

export function useAdminUser(userId: string): UseQueryResult<AdminUserDetail, ApiError> {
  return useQuery({
    queryKey: adminKeys.user(userId),
    queryFn: () =>
      unwrap(api.GET("/api/v1/admin/users/{user_id}", { params: { path: { user_id: userId } } })),
  });
}

export function useAdminRoles(): UseQueryResult<AdminRole[], ApiError> {
  return useQuery({
    queryKey: adminKeys.roles(),
    queryFn: () => unwrap(api.GET("/api/v1/admin/roles", {})),
    // Le référentiel des rôles ne bouge qu'à l'initiative d'un administrateur :
    // le recharger à chaque montage d'écran serait du trafic pour rien.
    staleTime: 5 * 60 * 1000,
  });
}

interface ModerationInput {
  userId: string;
  reason: string;
}

/**
 * Invalide la fiche, la liste et le journal après une action.
 *
 * Les trois, parce que les trois affichent l'état modifié : n'en rafraîchir
 * qu'une laisse les autres afficher un statut périmé jusqu'au rechargement.
 */
function useAfterModeration() {
  const queryClient = useQueryClient();
  return (userId: string) => {
    void queryClient.invalidateQueries({ queryKey: adminKeys.user(userId) });
    void queryClient.invalidateQueries({ queryKey: adminKeys.allUsers() });
    void queryClient.invalidateQueries({ queryKey: adminKeys.auditLogs() });
  };
}

export function useSuspendUser(): UseMutationResult<AdminUser, ApiError, ModerationInput> {
  const afterModeration = useAfterModeration();
  return useMutation({
    mutationFn: ({ userId, reason }: ModerationInput) =>
      unwrap(
        api.POST("/api/v1/admin/users/{user_id}/suspend", {
          params: { path: { user_id: userId } },
          body: { reason },
        }),
      ),
    onSuccess: (_data, { userId }) => {
      afterModeration(userId);
    },
  });
}

export function useReactivateUser(): UseMutationResult<AdminUser, ApiError, ModerationInput> {
  const afterModeration = useAfterModeration();
  return useMutation({
    mutationFn: ({ userId, reason }: ModerationInput) =>
      unwrap(
        api.POST("/api/v1/admin/users/{user_id}/reactivate", {
          params: { path: { user_id: userId } },
          body: { reason },
        }),
      ),
    onSuccess: (_data, { userId }) => {
      afterModeration(userId);
    },
  });
}

interface RoleChangeInput extends ModerationInput {
  role: string;
}

export function useGrantRole(): UseMutationResult<unknown, ApiError, RoleChangeInput> {
  const afterModeration = useAfterModeration();
  return useMutation({
    mutationFn: ({ userId, role, reason }: RoleChangeInput) =>
      unwrap(
        api.POST("/api/v1/admin/users/{user_id}/roles", {
          params: { path: { user_id: userId } },
          body: { role, reason },
        }),
      ),
    onSuccess: (_data, { userId }) => {
      afterModeration(userId);
    },
  });
}

export function useRevokeRole(): UseMutationResult<unknown, ApiError, RoleChangeInput> {
  const afterModeration = useAfterModeration();
  return useMutation({
    mutationFn: ({ userId, role, reason }: RoleChangeInput) =>
      unwrap(
        api.POST("/api/v1/admin/users/{user_id}/roles/{role_slug}/revoke", {
          params: { path: { user_id: userId, role_slug: role } },
          body: { reason },
        }),
      ),
    onSuccess: (_data, { userId }) => {
      afterModeration(userId);
    },
  });
}

interface AuditPage {
  data: AuditEntry[];
  meta: components["schemas"]["PageMeta"];
}

export function useAuditLog(
  action?: string,
): UseInfiniteQueryResult<InfiniteData<AuditPage>, ApiError> {
  return useInfiniteQuery({
    queryKey: adminKeys.auditLog(action),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/v1/admin/audit-logs", {
          params: {
            query: {
              ...(action ? { action } : {}),
              ...(pageParam === undefined ? {} : { cursor: pageParam }),
            },
          },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
  });
}

export type OutboxEntry = components["schemas"]["OutboxEntryResponse"];
export type OutboxPage = components["schemas"]["OutboxListResponse"];
export type OutboxView = NonNullable<
  paths["/api/v1/admin/outbox"]["get"]["parameters"]["query"]
>["view"];

/**
 * Vue de l'outbox (docs/15 §9).
 *
 * `refetchInterval` : c'est le seul écran de l'application qu'on ouvre **pendant**
 * un incident, et un opérateur qui vient de corriger un consommateur veut voir la
 * file se vider sans recharger la page. Dix secondes, pas moins : ce n'est pas un
 * flux temps réel, et interroger la base plus souvent que le publisher ne
 * travaille n'apprendrait rien de plus.
 */
export function useOutbox(view: OutboxView): UseQueryResult<OutboxPage, ApiError> {
  return useQuery({
    queryKey: adminKeys.outboxView(view ?? "unpublished"),
    queryFn: () => unwrap(api.GET("/api/v1/admin/outbox", { params: { query: { view } } })),
    refetchInterval: 10_000,
  });
}

export interface ReplayInput {
  eventId: string;
  reason: string;
}

export function useReplayEvent(): UseMutationResult<unknown, ApiError, ReplayInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, reason }: ReplayInput) =>
      unwrap(
        api.POST("/api/v1/admin/outbox/{event_id}/replay", {
          params: { path: { event_id: eventId } },
          body: { reason },
        }),
      ),
    // Le préfixe entier : un rejeu fait changer l'événement de vue — il quitte
    // « en échec » pour « en attente » — et n'invalider que celle qu'on regarde
    // laisserait l'autre afficher une ligne qui n'y est plus.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.outbox() }),
  });
}

// ── Modération (docs/16 §2, docs/13 §6) ──────────────────────────────────────

export type Report = components["schemas"]["ReportResponse"];
export type ReportPage = components["schemas"]["ReportListResponse"];
export type ModeratedContent = components["schemas"]["ModeratedContentResponse"];
export type ModerationAction = components["schemas"]["ModerationAction"];

export function useReports(openOnly = true): UseQueryResult<ReportPage, ApiError> {
  return useQuery({
    queryKey: adminKeys.reportsView(openOnly),
    queryFn: () =>
      unwrap(api.GET("/api/v1/admin/reports", { params: { query: { open_only: openOnly } } })),
  });
}

/**
 * Le contenu signalé — **chargé à la demande, jamais avec la fiche**.
 *
 * ⚠️ `enabled` porte ici une garantie, pas une optimisation. Chaque appel de
 * cette route écrit une entrée au journal d'audit (docs/13 §6) : la déclencher
 * en montant l'écran remplirait le journal de consultations que personne n'a
 * voulues, et le rendrait illisible le jour où il faut prouver qui a lu quoi.
 *
 * `staleTime: Infinity` et `retry: false` pour la même raison : ni
 * rechargement automatique, ni seconde tentative. Une divulgation est un geste,
 * et un geste ne se répète pas tout seul.
 */
export function useReportedContent(
  reportId: string | null,
): UseQueryResult<ModeratedContent, ApiError> {
  return useQuery({
    queryKey: adminKeys.reportContent(reportId ?? "none"),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/admin/reports/{report_id}/content", {
          params: { path: { report_id: reportId as string } },
        }),
      ),
    enabled: reportId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export interface ResolveReportInput {
  reportId: string;
  action: ModerationAction;
  note: string;
}

export function useResolveReport(): UseMutationResult<Report, ApiError, ResolveReportInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, action, note }: ResolveReportInput) =>
      unwrap(
        api.POST("/api/v1/admin/reports/{report_id}/resolve", {
          params: { path: { report_id: reportId } },
          body: { action, note },
        }),
      ),
    // Le préfixe entier : un dossier tranché quitte la file **et** change de
    // statut dans sa fiche. Une décision peut aussi masquer un message ou
    // suspendre un compte, ce que la liste des utilisateurs affiche.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.reports() });
      void queryClient.invalidateQueries({ queryKey: adminKeys.allUsers() });
    },
  });
}

// ── Statistiques (docs/16 §8) ────────────────────────────────────────────────

export type PlatformSnapshot = components["schemas"]["PlatformSnapshotResponse"];
export type GrowthSeries = components["schemas"]["GrowthResponse"];

/**
 * L'état de la plateforme, maintenant.
 *
 * Aucun `refetchInterval` : ce n'est pas un écran de supervision, et un
 * rafraîchissement automatique ferait courir six requêtes d'agrégation tant
 * qu'un onglet reste ouvert. L'écran de l'outbox, lui, en a un — parce qu'on
 * l'ouvre pendant un incident et qu'on regarde une file bouger.
 */
export function usePlatformStats(): UseQueryResult<PlatformSnapshot, ApiError> {
  return useQuery({
    queryKey: adminKeys.statsOverview(),
    queryFn: () => unwrap(api.GET("/api/v1/admin/stats/overview", {})),
  });
}

export function useGrowth(days: number): UseQueryResult<GrowthSeries, ApiError> {
  return useQuery({
    queryKey: adminKeys.statsGrowth(days),
    queryFn: () => unwrap(api.GET("/api/v1/admin/stats/growth", { params: { query: { days } } })),
  });
}

// ── Feature flags (docs/05 §11) ──────────────────────────────────────────────

export type FeatureFlag = components["schemas"]["FeatureFlagResponse"];

export function useFeatureFlags(): UseQueryResult<FeatureFlag[], ApiError> {
  return useQuery({
    queryKey: adminKeys.featureFlags(),
    queryFn: () => unwrap(api.GET("/api/v1/admin/feature-flags", {})),
  });
}

interface FlagInput {
  key: string;
  enabled: boolean;
  rules: Record<string, unknown>;
}

/**
 * Écrit l'état **complet** du flag, jamais un delta.
 *
 * L'écran envoie ce qu'il a sous les yeux : `enabled` et `rules` ensemble. Un
 * envoi partiel laisserait en place les règles d'une campagne passée, et
 * rallumer un flag ne le rouvrirait qu'aux dix pour cent d'il y a trois mois —
 * sans que rien ne le dise.
 */
export function useSetFeatureFlag(): UseMutationResult<FeatureFlag, ApiError, FlagInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled, rules }: FlagInput) =>
      unwrap(
        api.PATCH("/api/v1/admin/feature-flags/{key}", {
          params: { path: { key } },
          body: { enabled, rules },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.featureFlags() });
      // Une bascule apparaît au journal d'audit : le laisser périmé ferait
      // croire que le geste n'a pas été tracé.
      void queryClient.invalidateQueries({ queryKey: adminKeys.auditLogs() });
    },
  });
}
